import { useEffect, useState } from "react";

import { css } from "@emotion/react";
import MediaPlaceholder from "@hl/base-components/lib/MediaPlaceholder";
import { SHADOW_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { SupportedMediaType } from "@hl/communities-app/lib/apollo/graphql.generated";
import useAnalytics from "@hl/shared-features/lib/features/analytics/useAnalytics";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks/useFeatureFlags";
import { logError } from "@hl/shared-features/lib/services/logger";
import {
  AspectRatio,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Group,
  Image,
  Space,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { Dropzone, FileWithPath, MIME_TYPES } from "@mantine/dropzone";
import { IconTrash, IconUpload, IconX } from "@tabler/icons";
import { FileRejection } from "react-dropzone";

import { ReactComponent as IconPhoto } from "assets/icons/photo.svg";
import {
  increasedSizeMbByType,
  mimeFromType,
  sizeMbByType,
  typeFromMime,
} from "utils/media/file";
import { SupportedMediaMime } from "~/apollo/graphql.generated";

import { useGetUploadUrlForContractUriMutation } from "../../../tokens/queries/hosted-media.graphql.generated";

export const uploadToS3 = async (file: File, url: string) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-type": file.type,
    },
  });

  if (!response.ok) {
    throw new Error(
      `[CollectionMediaUploader.uploadToS3] error response from s3: ${JSON.stringify(
        response
      )}`
    );
  }
};

export const CreationPreviewImage = ({
  url,
  mediaType,
  tiny = false,
  width = "256px",
}: {
  url?: string | null;
  mediaType?: SupportedMediaType | "zip" | null;
  tiny?: boolean;
  width?: string;
}) => {
  const theme = useMantineTheme();
  if (!url || !mediaType) {
    return <MediaPlaceholder size={tiny ? "64px" : width} iconSize={24} />;
  }
  return (
    <>
      {mediaType === SupportedMediaType.VIDEO && !tiny && (
        <video controls loop playsInline width="100%">
          <source src={url} />
        </video>
      )}
      {mediaType === SupportedMediaType.VIDEO && tiny && (
        <video controls loop playsInline height="64px">
          <source src={url} />
        </video>
      )}
      {mediaType === SupportedMediaType.IMAGE && (
        <Image
          src={url}
          width={tiny ? "64px" : width}
          fit="contain"
          styles={{
            image: {
              border: `0.5px solid ${theme.colors.divider[0]}`,
            },
          }}
          imageProps={{ onLoad: () => URL.revokeObjectURL(url) }}
        />
      )}
      {mediaType === SupportedMediaType.AUDIO && (
        <audio controls controlsList="nodownload" style={{ width: width }}>
          <source src={url} />
        </audio>
      )}
    </>
  );
};

export interface Props {
  onMediaChanged: (
    media: {
      url: string;
      type: string;
    } | null
  ) => void;
  mediaError: string;
  mediaUrl?: string | null;
  mediaType: SupportedMediaType;
  isCoverImage?: boolean;
  width?: string;
}

export const UPLOAD_ERROR =
  "Could not upload media! Refresh and try again, or contact support!";

const CollectionMediaUploader = ({
  mediaError: errorFromProps,
  onMediaChanged,
  mediaUrl,
  mediaType,
  isCoverImage = false,
  width = "256px",
}: Props) => {
  const [media, setMedia] = useState<FileWithPath | null>(null);
  const [localMediaUrl, setLocalMediaUrl] = useState<string | undefined | null>(
    mediaUrl
  );
  const [mediaError, setMediaError] = useState<string>(errorFromProps);
  const [isMediaUploading, setIsMediaUploading] = useState<boolean>(false);
  const { trackEvent, EventType } = useAnalytics();
  const [getUploadUrl] = useGetUploadUrlForContractUriMutation();
  const [rejections, setRejections] = useState<FileRejection[]>([]);
  const increaseZipLimit = useFeatureFlags(
    FEATURE_FLAGS.ZIP_INCREASE_FILE_SIZE_LIMIT
  );

  useEffect(() => {
    setMediaError(errorFromProps);
  }, [errorFromProps]);

  const onFileSelected = (files: FileWithPath[]) => {
    const file = files[0];

    const mediaType = typeFromMime[file.type];
    if (mediaType === SupportedMediaType.AUDIO) {
      trackEvent(EventType.DeployCollection_MediaSelectedAudioType);
    } else if (mediaType === SupportedMediaType.VIDEO) {
      trackEvent(EventType.DeployCollection_MediaSelectedVideoType);
    } else if (mediaType === SupportedMediaType.IMAGE) {
      trackEvent(EventType.DeployCollection_MediaSelectedImageType);
    }

    const maxSizeInMb = increaseZipLimit
      ? increasedSizeMbByType[mediaType]
      : sizeMbByType[mediaType];
    if (file.size > maxSizeInMb * 1000000) {
      const error = `The file size cannot be greater than ${maxSizeInMb}MB.`;
      setMediaError(error);
    } else {
      setMedia(file);
      setMediaError("");
    }
  };

  const clearAllAttachments = () => {
    setMedia(null);
    setMediaError(errorFromProps);
    onMediaChanged(null);
  };
  const getMediaUrls = async (mime: SupportedMediaMime) => {
    const getUrlResponse = await getUploadUrl({
      variables: { data: { mime } },
    });

    const metadata = getUrlResponse?.data?.getUploadUrlForContractUri?.metadata;
    if (!metadata) {
      throw new Error(
        `[CollectionMediaUploader] Unexpected response from getUploadUrlForContractUri: ${JSON.stringify(
          { getUrlResponse }
        )}`
      );
    }

    const { url, publicUrl } = metadata;
    if (!url || !publicUrl) {
      throw new Error(
        `[CollectionMediaUploader] Unexpected upload media metadata: ${JSON.stringify(
          { metadata }
        )}`
      );
    }

    return { url, publicUrl };
  };

  /**
   * Upload media files and replaces the local blob URLs with the public CDN ones.
   * @param file file to be uploaded
   */
  const uploadMediaFileToS3 = async (file: File) => {
    try {
      setIsMediaUploading(true);
      const { url, publicUrl } = await getMediaUrls(
        mimeFromType[file.type] as SupportedMediaMime
      );
      await uploadToS3(file, url);
      onMediaChanged({ url: publicUrl, type: file.type });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err) {
      logError({
        err,
        message: "[CollectionMediaUploader] Failed to fetch upload URL",
      });

      setMediaError(UPLOAD_ERROR);
      setMedia(null);
      onMediaChanged(null);
    } finally {
      setIsMediaUploading(false);
    }
  };

  useEffect(() => {
    if (media) {
      setLocalMediaUrl(URL.createObjectURL(media));
      uploadMediaFileToS3(media); // runs async
    }
  }, [media]);
  const theme = useMantineTheme();

  const mediaPreview = (mediaUrl || media) &&
    localMediaUrl &&
    !isMediaUploading && (
      <Stack sx={{ position: "relative" }} spacing={10}>
        {isCoverImage && (
          <Text size="sm" fw={WEIGHT_BOLD}>
            Preview image
          </Text>
        )}
        <CreationPreviewImage
          width={width}
          url={localMediaUrl}
          mediaType={media?.type ? typeFromMime[media.type] : mediaType}
        />
        <Button
          styles={{
            label: {
              gap: 4,
            },
          }}
          px={8}
          color={SHADOW_COLOR}
          size="xs"
          onClick={() => clearAllAttachments()}
        >
          <IconTrash size={16} color={theme.colors.errorStatus[0]} />
          <Text size="xs" fw={WEIGHT_BOLD} color={theme.colors.errorStatus[0]}>
            Remove
          </Text>
        </Button>
        <Group position="center">
          {mediaError && (
            <Text size="sm" color={STATUS_COLOR.ERROR}>
              {mediaError}
            </Text>
          )}
        </Group>
      </Stack>
    );
  const supportedTypes = isCoverImage
    ? [MIME_TYPES.jpeg, MIME_TYPES.png]
    : [
        MIME_TYPES.jpeg,
        MIME_TYPES.png,
        MIME_TYPES.webp,
        MIME_TYPES.gif,
        "video/webm",
        MIME_TYPES.mp4,
        "audio/mpeg",
        "audio/wav",
      ];
  const mediaUpload = (!media || isMediaUploading) && (
    <Box
      sx={() => ({
        minHeight: "96px",
        width: width,
      })}
    >
      {isCoverImage && (
        <Text size="sm" fw={WEIGHT_BOLD} mb={8}>
          Preview image
        </Text>
      )}
      <Dropzone
        multiple={false}
        accept={supportedTypes}
        onDrop={async (files) => {
          setRejections([]);
          onFileSelected(files);
        }}
        onReject={(files) => {
          setRejections(files);
        }}
        loading={isMediaUploading}
        radius={4}
        sx={(theme) => ({
          borderColor: mediaError ? theme.colors.red[4] : theme.colors.black[0],
          root: {
            width: "100%",
            height: "100%",
            borderWidth: "1px",
            borderColor: mediaError
              ? theme.colors.red[4]
              : theme.colors.black[0],
            borderStyle: "solid",
          },
          inner: {
            width: "100%",
            height: "100%",
          },
        })}
      >
        {!isCoverImage && (
          <AspectRatio
            ratio={1}
            sx={() => ({
              width: "100%",
              height: "100%",
            })}
          >
            <Stack
              align="center"
              justify="center"
              spacing={0}
              style={{ pointerEvents: "none", width: "100%", height: "100%" }}
            >
              <Dropzone.Accept>
                <IconUpload
                  size={24}
                  stroke={1.5}
                  color={theme.fn.themeColor(TEXT_COLOR.PRIMARY)}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  size={24}
                  stroke={1.5}
                  color={theme.fn.themeColor(TEXT_COLOR.PRIMARY)}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto
                  css={css`
                    width: 24px;
                    height: 24px;
                  `}
                />
              </Dropzone.Idle>
              <Space h="lg" />
              <Text weight={500} size="sm" inline align="center">
                Add token media
              </Text>
              <Space h={8} />
              <Text
                size="sm"
                color={TEXT_COLOR.SECONDARY}
                inline
                align="center"
              >
                Use a PNG, JPG, WEBP, GIF, WebM, MP4, WAV or MP3. We recommend a
                size of at least 2000×2000 pixels for images.
              </Text>
            </Stack>
          </AspectRatio>
        )}
        {isCoverImage && (
          <Grid align="center" gutter={4} m={-4}>
            <Grid.Col span={3}>
              <Flex justify="center">
                <Dropzone.Accept>
                  <IconUpload
                    size={24}
                    stroke={1.5}
                    color={theme.fn.themeColor(TEXT_COLOR.PRIMARY)}
                  />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX
                    size={24}
                    stroke={1.5}
                    color={theme.fn.themeColor(TEXT_COLOR.PRIMARY)}
                  />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconPhoto
                    css={css`
                      width: 24px;
                      height: 24px;
                    `}
                  />
                </Dropzone.Idle>
              </Flex>
            </Grid.Col>
            <Grid.Col span={9}>
              <Text size="xs" color={TEXT_COLOR.SECONDARY} inline align="left">
                Upload a JPG, PNG, WEBP, or GIF that will be shown when audio
                and video files can’t be displayed.
              </Text>
            </Grid.Col>
          </Grid>
        )}
      </Dropzone>
      {rejections.map((rejection: FileRejection) => (
        <>
          <Text
            key={rejection.file.name + rejection.file.lastModified}
            mt="sm"
            size="sm"
            color={STATUS_COLOR.ERROR}
          >
            Error for {rejection.file.name}:
          </Text>
          {rejection.errors.map((error) => (
            <Text
              key={error.code}
              mt="sm"
              size="sm"
              color={STATUS_COLOR.ERROR}
              sx={() => ({ overflowWrap: "break-word" })}
            >
              {error.message}
            </Text>
          ))}
        </>
      ))}
      {mediaError && (
        <Center mt={6}>
          <Text size="sm" color={STATUS_COLOR.ERROR}>
            {mediaError}
          </Text>
        </Center>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        maxWidth: width,
      }}
      aria-invalid={!!mediaError}
    >
      {!mediaPreview && mediaUpload}
      {mediaPreview}
    </Box>
  );
};

export default CollectionMediaUploader;
